import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  ChevronDown, ChevronRight, Plus, Loader2, Pencil, Trash2,
  Scissors,
} from 'lucide-react';
import { toast } from 'sonner';
import SalesSectionForm from './SalesSectionForm';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => {
  const token = localStorage.getItem('auth_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Sale-eligible investment types. Mirrors backend SALE_ELIGIBLE_TYPES.
const ELIGIBLE_TYPES = new Set([
  'quoted_stocks', 'etf', 'mutual_fund', 'crypto', 'private_equity',
  'government_bond', 'corporate_bond',
  'real_estate', 'gold', 'convertible', 'safe', 'other',
]);

// Asset classes that only support full disposals (no partial sales, no units).
// For these, the form hides the mode toggle and fraction/units fields and
// auto-marks the holding as closed.
const FULL_SALE_ONLY_TYPES = new Set(['real_estate', 'convertible', 'safe', 'other']);

function fmtMoney(v, cur) {
  if (v == null || v === '') return '—';
  try {
    return `${cur ? cur + ' ' : ''}${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  } catch {
    return `${v}`;
  }
}

const emptyForm = (defaultCurrency, fullSaleOnly = false) => ({
  sale_date: new Date().toISOString().slice(0, 10),
  mode: 'fraction',
  fraction_sold: fullSaleOnly ? '1' : '',
  units_sold: '',
  units_total: '',
  proceeds_net: '',
  currency: defaultCurrency || '',
  cost_basis_override: '',
  close_holding: fullSaleOnly, // forced true for full-sale-only types
  notes: '',
  external_reference: '',
});

/**
 * Collapsible Sales / Realised-Gains section for the edit-investment modal.
 *
 * Only renders for sale-eligible investment types. Persists to
 * /api/investments/{id}/sales and feeds Section C of the Tax Data Pack.
 */
export default function SalesSection({ investment, onClosedChange }) {
  const eligible = investment && ELIGIBLE_TYPES.has(investment.type);
  const fullSaleOnly = investment && FULL_SALE_ONLY_TYPES.has(investment.type);
  const investmentId = investment?.investment_id;
  const defaultCurrency = investment?.currency || '';

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sales, setSales] = useState([]);
  const [closedFlag, setClosedFlag] = useState(Boolean(investment?.closed));
  const [addOpen, setAddOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm(defaultCurrency, fullSaleOnly));
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [togglingClosed, setTogglingClosed] = useState(false);

  const fetchSales = async () => {
    if (!eligible || !investmentId) return;
    setLoading(true);
    try {
      const resp = await fetch(`${BACKEND_URL}/api/investments/${investmentId}/sales`, { headers: authHeaders() });
      if (!resp.ok) throw new Error(`Failed to load (${resp.status})`);
      const data = await resp.json();
      setSales(data.sales || []);
      setClosedFlag(Boolean(data.investment_closed));
    } catch (e) {
      toast.error(e.message || 'Failed to load sales');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && sales.length === 0 && !loading) {
      fetchSales();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const resetForm = () => {
    setForm(emptyForm(defaultCurrency, fullSaleOnly));
    setEditingId(null);
    setAddOpen(false);
  };

  const startEdit = (s) => {
    setEditingId(s.sale_id);
    setForm({
      sale_date: s.sale_date || '',
      mode: s.mode || 'fraction',
      fraction_sold: s.mode === 'fraction' ? (s.fraction_sold ?? '') : '',
      units_sold: s.units_sold ?? '',
      units_total: s.units_total ?? '',
      proceeds_net: s.proceeds_net ?? '',
      currency: s.currency || defaultCurrency,
      cost_basis_override: s.cost_basis_overridden ? (s.cost_basis_used ?? '') : '',
      close_holding: false, // not edited here
      notes: s.notes || '',
      external_reference: s.external_reference || '',
    });
    setAddOpen(true);
  };

  const handleSave = async (e) => {
    e?.preventDefault?.();
    if (!form.sale_date) { toast.error('Sale date is required'); return; }
    const proceeds = Number(form.proceeds_net);
    if (proceeds < 0 || Number.isNaN(proceeds)) { toast.error('Proceeds must be ≥ 0'); return; }

    const payload = {
      sale_date: form.sale_date,
      mode: fullSaleOnly ? 'fraction' : form.mode,
      proceeds_net: proceeds,
      currency: (form.currency || defaultCurrency || '').toUpperCase() || undefined,
    };
    if (fullSaleOnly) {
      payload.fraction_sold = 1.0;
    } else if (form.mode === 'fraction') {
      const f = Number(form.fraction_sold);
      if (!f || f <= 0 || f > 1) { toast.error('Fraction must be between 0 and 1'); return; }
      payload.fraction_sold = f;
    } else {
      const us = Number(form.units_sold);
      const ut = Number(form.units_total);
      if (!us || us <= 0 || !ut || ut <= 0 || us > ut) {
        toast.error('Units sold must be > 0 and ≤ units total'); return;
      }
      payload.units_sold = us;
      payload.units_total = ut;
    }
    if (form.cost_basis_override !== '' && Number(form.cost_basis_override) >= 0) {
      payload.cost_basis_override = Number(form.cost_basis_override);
    }
    if (!editingId) {
      payload.close_holding = fullSaleOnly ? true : Boolean(form.close_holding);
    }
    if (form.notes) payload.notes = form.notes;
    if (form.external_reference) payload.external_reference = form.external_reference;

    setSaving(true);
    try {
      const url = editingId
        ? `${BACKEND_URL}/api/investments/${investmentId}/sales/${editingId}`
        : `${BACKEND_URL}/api/investments/${investmentId}/sales`;
      const method = editingId ? 'PUT' : 'POST';
      const resp = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(payload),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to save');
      }
      toast.success(editingId ? 'Sale updated' : 'Sale recorded');
      resetForm();
      await fetchSales();
      if (!editingId && payload.close_holding && onClosedChange) onClosedChange(true);
    } catch (e) {
      toast.error(e.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (s) => {
    if (!window.confirm('Delete this sale? This can\'t be undone.')) return;
    setDeletingId(s.sale_id);
    try {
      const resp = await fetch(`${BACKEND_URL}/api/investments/${investmentId}/sales/${s.sale_id}`, {
        method: 'DELETE', headers: authHeaders(),
      });
      if (!resp.ok) throw new Error(`Delete failed (${resp.status})`);
      toast.success('Sale removed');
      setSales((prev) => prev.filter((x) => x.sale_id !== s.sale_id));
    } catch (e) {
      toast.error(e.message || 'Failed to delete');
    } finally {
      setDeletingId(null);
    }
  };

  const toggleClosed = async (next) => {
    setTogglingClosed(true);
    try {
      const resp = await fetch(`${BACKEND_URL}/api/investments/${investmentId}/closed`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ closed: next }),
      });
      if (!resp.ok) throw new Error(`Failed (${resp.status})`);
      setClosedFlag(next);
      toast.success(next ? 'Holding marked closed' : 'Holding reopened');
      if (onClosedChange) onClosedChange(next);
    } catch (e) {
      toast.error(e.message || 'Failed to update');
    } finally {
      setTogglingClosed(false);
    }
  };

  if (!eligible) return null;

  const lifetimeRealised = sales.reduce((acc, s) => acc + Number(s.realised_gain_loss || 0), 0);

  return (
    <div className="border border-slate-200 rounded-lg bg-slate-50/50" data-testid="sales-section">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-slate-100/50 rounded-lg"
        data-testid="sales-section-toggle"
      >
        <div className="flex items-center gap-2">
          {open ? <ChevronDown className="w-4 h-4 text-slate-500" /> : <ChevronRight className="w-4 h-4 text-slate-500" />}
          <Scissors className="w-4 h-4 text-emerald-700" />
          <span className="text-sm font-semibold text-slate-800">Sales &amp; Realised Gains</span>
          {sales.length > 0 && (
            <Badge variant="outline" className="bg-emerald-50 text-emerald-800 border-emerald-300 text-xs">
              {sales.length} recorded
            </Badge>
          )}
          {closedFlag && (
            <Badge variant="outline" className="bg-slate-200 text-slate-700 border-slate-300 text-xs">
              Closed
            </Badge>
          )}
        </div>
        <span className="text-xs text-slate-500 hidden sm:inline">{open ? 'Hide' : 'Manage'} sales</span>
      </button>

      {open && (
        <div className="px-4 pb-4 space-y-3" data-testid="sales-section-body">
          {loading ? (
            <div className="flex items-center text-sm text-slate-500 py-3">
              <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading…
            </div>
          ) : sales.length === 0 && !addOpen ? (
            <p className="text-xs text-slate-500 py-2">
              No sales recorded yet. They&apos;ll feed Section C (Cycles completed) of your Tax Data Pack once added.
            </p>
          ) : (
            <div className="space-y-2" data-testid="sales-section-list">
              {sales.map((s) => {
                const gain = Number(s.realised_gain_loss || 0);
                return (
                  <div
                    key={s.sale_id}
                    className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-xs"
                    data-testid={`sale-row-${s.sale_id}`}
                  >
                    <div className="flex flex-col">
                      <span className="text-slate-900">
                        <span className="font-medium">{s.sale_date}</span>{' · '}
                        {fullSaleOnly
                          ? <span className="text-slate-700">Full disposal</span>
                          : s.mode === 'units' && s.units_sold && s.units_total
                            ? <span className="text-slate-700">{s.units_sold} / {s.units_total} units</span>
                            : <span className="text-slate-700">{(Number(s.fraction_sold || 0) * 100).toFixed(2)}%</span>}
                        {' · proceeds '}
                        <span className="font-medium">{fmtMoney(s.proceeds_net, s.currency)}</span>
                      </span>
                      <span className="text-slate-500 mt-0.5">
                        Cost basis {fmtMoney(s.cost_basis_used, s.currency)}
                        {s.cost_basis_overridden ? ' (overridden)' : ''}
                        {' · '}
                        <span className={gain >= 0 ? 'text-emerald-700 font-medium' : 'text-red-600 font-medium'}>
                          G/L {fmtMoney(gain, s.currency)}
                        </span>
                      </span>
                      {s.notes && <span className="text-slate-500 mt-0.5">{s.notes}</span>}
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        type="button" size="sm" variant="ghost"
                        onClick={() => startEdit(s)}
                        className="h-7 w-7 p-0 text-slate-500 hover:text-emerald-700"
                        data-testid={`sale-edit-${s.sale_id}`}
                        aria-label="Edit"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button
                        type="button" size="sm" variant="ghost"
                        onClick={() => handleDelete(s)}
                        disabled={deletingId === s.sale_id}
                        className="h-7 w-7 p-0 text-slate-500 hover:text-red-600"
                        data-testid={`sale-delete-${s.sale_id}`}
                        aria-label="Delete"
                      >
                        {deletingId === s.sale_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                      </Button>
                    </div>
                  </div>
                );
              })}
              {sales.length > 0 && (
                <div className="text-[11px] text-slate-600 px-1" data-testid="sales-section-lifetime">
                  Lifetime realised G/L on this holding: <span className={lifetimeRealised >= 0 ? 'text-emerald-700 font-medium' : 'text-red-600 font-medium'}>
                    {fmtMoney(lifetimeRealised, defaultCurrency)}
                  </span>
                </div>
              )}
            </div>
          )}

          {addOpen ? (
            <SalesSectionForm
              form={form}
              setForm={setForm}
              fullSaleOnly={fullSaleOnly}
              editingId={editingId}
              saving={saving}
              defaultCurrency={defaultCurrency}
              onCancel={resetForm}
              onSave={handleSave}
            />
          ) : (
            <Button
              type="button" variant="outline" size="sm" onClick={() => setAddOpen(true)}
              className="text-emerald-800 border-emerald-300 hover:bg-emerald-50"
              data-testid="sale-add-btn"
            >
              <Plus className="w-3.5 h-3.5 mr-1" /> Record a sale
            </Button>
          )}

          <div className="flex items-center justify-between gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 mt-2">
            <div className="text-xs text-slate-700">
              <div className="font-medium">Closed holding</div>
              <div className="text-slate-500">When closed, this row is hidden from your dashboard but stays in &quot;Closed Holdings&quot; and the Tax Data Pack.</div>
            </div>
            <div className="flex items-center gap-2">
              {togglingClosed && <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400" />}
              <Switch
                checked={closedFlag}
                onCheckedChange={toggleClosed}
                disabled={togglingClosed}
                data-testid="sales-section-closed-toggle"
              />
            </div>
          </div>

          <p className="text-[11px] text-slate-500 mt-1">
            {fullSaleOnly
              ? 'For this asset type, every sale is recorded as a full disposal.'
              : 'Cost basis defaults to your original cost × fraction (single-lot). Override if you used a different basis.'}
          </p>
        </div>
      )}
    </div>
  );
}
